/**
 * Os sete eixos de leitura do quadro de verificação (E78.F3; Arquivo na E84.F2).
 *
 * O quadro devolvido pelo motor é uma pilha de onze seções em que quase tudo
 * aparece com o mesmo peso: no caso real GT-2026-0005 o perito rolava sete
 * linhas de assinatura embutida para ler "não se aplica" antes de chegar ao
 * único item que de fato divergia. Este módulo não muda o quadro — ele só diz
 * **onde cada dado que o backend já devolveu deve ser lido** e **quanta
 * divergência há em cada eixo**, para que o eixo divergente abra primeiro e o
 * eixo em que tudo confere fique só no contador.
 *
 * A régua da divergência é a do próprio motor
 * (`services/exame_eletronico_identidade.py`): **divergência é contradição, não
 * falta de dado**. Só `nao_confere` conta. `ausente` e `nao_executado` são
 * ausências e limitações declaradas — elas continuam saindo por escrito no eixo
 * Alertas, e contá-las aqui inventaria um confronto que o exame não fez.
 */
import type {
  ItemVerificacaoData,
  ResultadoExameEletronicoData,
  SituacaoItem,
} from '@/lib/exame_eletronico';
import { contratoDoResultado, itensDoContrato } from '@/lib/exame_eletronico_contrato';

export type ChaveEixo =
  | 'integridade'
  | 'arquivo'
  | 'identidade'
  | 'trilha'
  | 'rede'
  | 'localizacao'
  | 'contrato'
  | 'alertas';

/**
 * Ordem de leitura do quadro — e o desempate de qual eixo abre primeiro. O eixo `contrato`
 * (E98.F7) não está aqui: ele entra antes de Alertas só quando o resultado traz o kit ou o apoio.
 */
export const ORDEM_DOS_EIXOS: readonly ChaveEixo[] = [
  'integridade',
  'arquivo',
  'identidade',
  'trilha',
  'rede',
  'localizacao',
  'alertas',
];

export interface EixoDoQuadro {
  chave: ChaveEixo;
  /** Itens que o exame apurou em CONTRADIÇÃO com o protocolo (`nao_confere`). */
  divergencias: number;
  /** Quantos itens o eixo examinou — é o que o contador mostra quando tudo confere. */
  examinados: number;
}

function contarDivergentes(itens: readonly ItemVerificacaoData[]): number {
  return itens.filter((item) => item.situacao === 'nao_confere').length;
}

/** Situação de um estágio externo pela chave; ausente quando o estágio não veio. */
function situacaoDoEstagio(
  enriquecimento: readonly ItemVerificacaoData[],
  chave: string,
): SituacaoItem | null {
  return enriquecimento.find((item) => item.chave === chave)?.situacao ?? null;
}

function divergentesEntreEstagios(
  enriquecimento: readonly ItemVerificacaoData[],
  chaves: readonly string[],
): number {
  return chaves.filter((chave) => situacaoDoEstagio(enriquecimento, chave) === 'nao_confere')
    .length;
}

/**
 * O eixo Integridade fecha com a assinatura embutida no arquivo.
 *
 * Só o veredito `nao_confirmada` é divergência: `nao_se_aplica` (o arquivo não
 * traz assinatura criptográfica) e `nao_foi_possivel_confirmar` são estados do
 * método, não contradição. Exame anterior ao estágio (`assinatura_digital`
 * nulo) não examinou nada — nem conta nem diverge.
 */
function assinaturaEmbutida(resultado: ResultadoExameEletronicoData): {
  divergencias: number;
  examinados: number;
} {
  if (!resultado.assinatura_digital) return { divergencias: 0, examinados: 0 };
  return {
    divergencias: resultado.assinatura_digital_veredito === 'nao_confirmada' ? 1 : 0,
    examinados: 1,
  };
}

/** Eventos que contradizem a trilha: fora de ordem ou com UTC e local incoerentes. */
function eventosDivergentes(resultado: ResultadoExameEletronicoData): number {
  return resultado.linha_do_tempo.filter(
    (evento) => evento.fora_de_ordem || evento.utc_local_incoerentes,
  ).length;
}

/**
 * A cronologia entre fontes (E84.F4) entra no eixo Trilha: são os quatro itens
 * que confrontam registro, assinatura embutida e custódia contra o ato e o
 * formato das datas. Exame anterior ao estágio (`jornada` nulo) não examinou
 * nada — nem conta nem diverge.
 */
function cronologiaDaJornada(
  resultado: ResultadoExameEletronicoData,
): readonly ItemVerificacaoData[] {
  return resultado.jornada?.itens ?? [];
}

/**
 * O eixo Arquivo (E84.F2): o que o próprio arquivo declara sobre si.
 *
 * Exame anterior ao estágio (`metadados` nulo) não examinou nada — nem conta
 * nem diverge, como a assinatura legada. `nao_confere` aqui é alerta médio no
 * backend, mas continua sendo divergência de LEITURA: o perito precisa abrir.
 */
function metadadosDoArquivo(
  resultado: ResultadoExameEletronicoData,
): readonly ItemVerificacaoData[] {
  return resultado.metadados?.itens ?? [];
}

/**
 * O vínculo da peça com a transação (E84.F3) entra no eixo Identidade: é o
 * mesmo confronto de "quem assinou e a que contratação isto pertence", agora
 * lido do texto da própria peça. Exame anterior ao estágio (`encadeamento`
 * nulo) não examinou nada — nem conta nem diverge.
 */
function vinculoDaPeca(resultado: ResultadoExameEletronicoData): readonly ItemVerificacaoData[] {
  // E157.F2: os códigos lidos das páginas são o mesmo confronto — o que a peça carrega impresso
  // contra o que o protocolo declara —, agora decodificado da imagem da página.
  return [...(resultado.encadeamento?.itens ?? []), ...(resultado.codigos_lidos?.itens ?? [])];
}

/** E98.F7: o eixo do contrato e dos documentos de apoio, quando o resultado os traz. */
function eixoDoContrato(resultado: ResultadoExameEletronicoData): EixoDoQuadro[] {
  const contrato = contratoDoResultado(resultado);
  if (contrato === null) return [];
  const itens = itensDoContrato(contrato);
  return [{ chave: 'contrato', divergencias: contarDivergentes(itens), examinados: itens.length }];
}

/** Reparte o quadro nos sete eixos (oito com o contrato), com o que examinou e o que divergiu. */
export function eixosDoQuadro(resultado: ResultadoExameEletronicoData): EixoDoQuadro[] {
  const assinatura = assinaturaEmbutida(resultado);
  const ipsDivergentes = resultado.ips.filter((ip) => ip.situacao === 'nao_confere').length;
  const arquivo = metadadosDoArquivo(resultado);
  const vinculo = vinculoDaPeca(resultado);
  const cronologia = cronologiaDaJornada(resultado);
  return [
    {
      chave: 'integridade',
      divergencias: contarDivergentes(resultado.integridade) + assinatura.divergencias,
      examinados: resultado.integridade.length + assinatura.examinados,
    },
    {
      chave: 'arquivo',
      divergencias: contarDivergentes(arquivo),
      examinados: arquivo.length,
    },
    {
      chave: 'identidade',
      divergencias: contarDivergentes(resultado.identidade) + contarDivergentes(vinculo),
      examinados: resultado.identidade.length + vinculo.length,
    },
    {
      chave: 'trilha',
      divergencias: eventosDivergentes(resultado) + contarDivergentes(cronologia),
      examinados: resultado.linha_do_tempo.length + cronologia.length,
    },
    {
      chave: 'rede',
      divergencias:
        ipsDivergentes + divergentesEntreEstagios(resultado.enriquecimento, ['classificacao_ip']),
      examinados: resultado.ips.length,
    },
    {
      chave: 'localizacao',
      // E168.F3.C01 (QA de 24/10): a coordenada sem precisão é limite da análise, não divergência.
      divergencias: divergentesEntreEstagios(resultado.enriquecimento, [
        'cluster_cadastral',
        'geocodificacao',
      ]),
      // E182.F1 (ADR-0135 §2.1): o endereço declarado medido é o resultado principal do eixo.
      examinados:
        (resultado.coordenada === null ? 0 : 1) +
        (typeof resultado.localizacao?.endereco_declarado?.distancia_m === 'number' ? 1 : 0) +
        resultado.cluster.length,
    },
    ...eixoDoContrato(resultado),
    {
      chave: 'alertas',
      divergencias: resultado.red_flags.length,
      examinados: resultado.red_flags.length,
    },
  ];
}

/**
 * Qual eixo abre primeiro: o divergente de menor ordem de leitura.
 *
 * Sem divergência nenhuma, abre Integridade — o começo da leitura. Nunca abre
 * "nenhum": um quadro sem painel aberto obrigaria um clique para ver qualquer
 * coisa, que é o oposto do que a Fase veio resolver.
 */
export function eixoQueAbrePrimeiro(eixos: readonly EixoDoQuadro[]): ChaveEixo {
  return eixos.find((eixo) => eixo.divergencias > 0)?.chave ?? 'integridade';
}
